import React, { useEffect, useState } from "react";
import { Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { actualizarPlanilla, crearPlanilla, mensajeError } from "../services/solicitudChequeService";

const vacia = (catalogos) => ({
  nombre: "", deposito: "", servicio: "General", monto: "", moneda: "Bolivianos", concepto: null, ciudad: catalogos.ciudad_usuario || "La Paz", obs: "", nro_contrato: "", nro_referencia: "",
});

// Crear o modificar una planilla (modelo de solicitud que se repite cada mes: luz, agua, alquileres…). Solo Contabilidad y gerencia.
export default function PlanillaFormDialog({ open, planilla, catalogos, onClose, onGuardada }) {
  const [form, setForm] = useState(() => vacia(catalogos));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    if (!planilla) return setForm(vacia(catalogos));
    const concepto = (catalogos.conceptos || []).find((c) => c.concepto === planilla.concepto && String(c.cuenta) === String(planilla.cuenta))
      || (catalogos.conceptos || []).find((c) => c.concepto === planilla.concepto) || null;
    setForm({
      nombre: planilla.nombre, deposito: planilla.deposito, servicio: planilla.servicio, monto: planilla.monto, moneda: planilla.moneda, concepto,
      ciudad: planilla.ciudad, obs: planilla.obs, nro_contrato: planilla.nro_contrato, nro_referencia: planilla.nro_referencia,
    });
  }, [open, planilla, catalogos]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.concepto) return setError("Elige el concepto.");
    setGuardando(true);
    setError("");
    try {
      const body = { ...form, concepto_id: form.concepto.id };
      if (planilla) await actualizarPlanilla(planilla.id, body); else await crearPlanilla(body);
      onGuardada(planilla ? "Planilla actualizada." : "Planilla creada.");
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la planilla."));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={guardar}>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>{planilla ? "Modificar planilla" : "Nueva planilla"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Autocomplete
            freeSolo size="small" options={catalogos.nombres || []} value={form.nombre} inputValue={form.nombre} onInputChange={(_, v) => setForm((f) => ({ ...f, nombre: v }))}
            renderInput={(p) => <TextField {...p} label="Dirigido a" required />}
          />
          <Autocomplete
            freeSolo size="small" options={catalogos.nombres || []} value={form.deposito} inputValue={form.deposito} onInputChange={(_, v) => setForm((f) => ({ ...f, deposito: v }))}
            renderInput={(p) => <TextField {...p} label="Depositado a" />}
          />
          <Autocomplete
            size="small" options={catalogos.conceptos || []} value={form.concepto} onChange={(_, v) => setForm((f) => ({ ...f, concepto: v }))}
            getOptionLabel={(o) => o.concepto || ""} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Concepto" required />}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField size="small" select label="Ciudad" value={form.ciudad} onChange={set("ciudad")}>
              {(catalogos.ciudades || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Tipo de servicio" value={form.servicio} onChange={set("servicio")}>
              {(catalogos.servicios || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField size="small" label="Monto habitual" value={form.monto} onChange={set("monto")} helperText="Opcional: se puede cambiar al enviar" sx={{ flex: 1 }} slotProps={{ htmlInput: { inputMode: "decimal" } }} />
            <ToggleButtonGroup exclusive size="small" value={form.moneda} onChange={(_, v) => v && setForm((f) => ({ ...f, moneda: v }))} sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700 } }}>
              <ToggleButton value="Bolivianos">Bs</ToggleButton>
              <ToggleButton value="Dolares">$us</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField size="small" label="Número de contrato" value={form.nro_contrato} onChange={set("nro_contrato")} />
            <TextField size="small" label="Número de referencia" value={form.nro_referencia} onChange={set("nro_referencia")} />
          </Box>
          <TextField size="small" label="Observación" multiline minRows={2} value={form.obs} onChange={set("obs")} slotProps={{ htmlInput: { maxLength: 250 } }} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
